import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Info,
  Pencil,
  PencilLine,
  Users,
  X,
} from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { type PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import { XStack, YStack } from "tamagui";
import { useApi } from "../../../core/apis";
import { getDayJs } from "../../../core/dayJs";
import { useUploadAvatar } from "../../../core/hooks/useUploadAvatar";
import { useStores } from "../../../core/stores";
import { isRunningInApp } from "../../../core/utils";
import { Body, BodyTextarea } from "../../../ui/Body";
import { Box } from "../../../ui/Box";
import { Button } from "../../../ui/Button";
import { Group } from "../../../ui/Group";
import { Label, LabelTextarea } from "../../../ui/Label";
import { LoadingCircular } from "../../../ui/LoadingCircular";
import { Screen } from "../../../ui/Screen";
import { Separator } from "../../../ui/Separator";
import { TitleTextarea } from "../../../ui/Title";
import { AvatarSize, UserAvatar } from "../../../ui/UserAvatar";
import { UserAvatarGroupRaw } from "../../../ui/UserAvatarGroupRaw";
import type UserModel from "../../../users/UserModel";
import type {
  BusinessPlanModel,
  BusinessStaticUserInfoWithUserUid,
  TeamStaticInfo,
} from "../BusinessPlanModel";

type Props = NativeStackScreenProps<
  ReactNavigation.RootParamList,
  "BusinessProfile"
>;

export const TeamRow = observer(
  ({
    team,
    businessPlan,
    hideTeamName,
    children,
  }: PropsWithChildren & {
    team: TeamStaticInfo;
    businessPlan: BusinessPlanModel;
    hideTeamName?: boolean;
  }) => {
    const showOwnTitle = !hideTeamName && team.title !== businessPlan.title;

    const users: BusinessStaticUserInfoWithUserUid[] = [];

    const displayNames: string[] = [];
    team.userUids.forEach((userUid) => {
      const info = businessPlan.data.userInfo[userUid];
      users.push({ userUid, ...info });
      displayNames.push(info.displayName);
    });

    return (
      <XStack
        key={team.uid}
        justifyContent="space-between"
        alignItems="center"
        width={"100%"}
      >
        <YStack gap={"$small"}>
          {showOwnTitle && <Label>{team.title}</Label>}
          <UserAvatarGroupRaw
            businessPlan={businessPlan}
            users={users}
            skipPadding
          />
          <Body>{displayNames.join(", ")}</Body>
        </YStack>

        <XStack gap={"$medium"}>{children}</XStack>
      </XStack>
    );
  },
);

export const CurrentTeamOrJoinList = observer(
  ({
    businessPlan,
    hideLeaveCta,
  }: {
    businessPlan: BusinessPlanModel;
    hideLeaveCta?: boolean;
  }) => {
    const { t } = useTranslation(); // 👈 Use translation hook

    const hasTeams =
      businessPlan.data.teamsInfo && businessPlan.data.teamsInfo.length > 0;
    const currentTeam = businessPlan.currentTeam;

    const showOwnTitle =
      currentTeam?.title && currentTeam?.title !== businessPlan.title;

    if (currentTeam) {
      return (
        <Box>
          <XStack gap={"$medium"} alignItems="center">
            <Users />
            <LabelTextarea
              icon={Pencil}
              focusOnIconPress
              defaultValue={
                showOwnTitle
                  ? currentTeam.title
                  : t("business:current_team_default_name")
              }
              submitOnBlur
              submitOnEnter
              blurOnSubmit
              onSubmitEditing={(e) => {
                void businessPlan.updateTeamTitle(
                  currentTeam.uid,
                  e.nativeEvent.text,
                );
              }}
            />
          </XStack>

          <TeamRow
            hideTeamName
            key={currentTeam.uid}
            team={currentTeam}
            businessPlan={businessPlan}
          >
            {!hideLeaveCta && (
              <Button
                type={"danger"}
                onPress={() => {
                  businessPlan.leaveTeam(currentTeam.uid);
                }}
              >
                {t("business:team_button_leave")}
              </Button>
            )}
          </TeamRow>
        </Box>
      );
    }

    return (
      <Box>
        <YStack gap={"$medium"}>
          {!hasTeams && (
            <YStack gap={"$medium"}>
              <Label>{t("business:team_create_label")}</Label>
              <Body>{t("business:team_create_instructions")}</Body>
            </YStack>
          )}

          {hasTeams && (
            <YStack gap={"$medium"}>
              <Label>{t("business:team_join_label")}</Label>
              <Body>{t("business:team_join_instructions")}</Body>
              <Group>
                {businessPlan.data.teamsInfo.map((team) => (
                  <TeamRow
                    key={team.uid}
                    team={team}
                    businessPlan={businessPlan}
                  >
                    <Button
                      type={"primary"}
                      onPress={() => {
                        businessPlan.joinTeam(team.uid);
                      }}
                    >
                      {t("business:team_button_join")}
                    </Button>
                  </TeamRow>
                ))}
              </Group>
              <Separator />
              <Body>{t("business:team_start_own_instructions")}</Body>
            </YStack>
          )}
          <Button
            type={"secondary"}
            icon={Users}
            onPress={() => {
              businessPlan.addNewTeam();
            }}
          >
            {t("business:team_button_create")}
          </Button>
        </YStack>
      </Box>
    );
  },
);

enum ProfileSection {
  Avatar = "avatar",
  ProfileQuestions = "profileQuestions",
  Description = "description",
  Team = "team",
}

const SectionWithInfo = observer(
  ({
    title,
    expanded,
    completed,
    onPress,
    onNextPress,
    children,
  }: PropsWithChildren & {
    title: string;
    expanded: boolean;
    completed: boolean;
    onPress: () => void;
    onNextPress?: () => void;
  }) => {
    const { t } = useTranslation();

    if (expanded) {
      return (
        <YStack gap={"$medium"}>
          {children}

          {!!onNextPress && (
            <Button
              alignSelf={"flex-end"}
              marginRight={"$larger"}
              onPress={onNextPress}
              icon={ChevronDown}
            >
              {t("business:section_button_next")}
            </Button>
          )}
        </YStack>
      );
    }

    return (
      <Box
        onPress={onPress}
        backgroundColor={completed ? undefined : "$backgroundBlock"}
      >
        <XStack
          gap={"$medium"}
          alignItems="center"
          justifyContent={"space-between"}
        >
          <XStack gap={"$medium"} alignItems="center">
            {completed ? <CheckCircle /> : <X />}
            <Label>{title}</Label>
          </XStack>
          <ChevronRight />
        </XStack>
      </Box>
    );
  },
);

export const BusinessProfileSettings = observer(
  ({
    sectionsEnabled,
    user,
    businessPlan,
  }: {
    user: UserModel;
    businessPlan: BusinessPlanModel;
    sectionsEnabled?: boolean;
  }) => {
    const isApp = isRunningInApp();
    const { sessionStore } = useStores();
    const { t } = useTranslation();

    const [currentSection, setCurrentSection] = React.useState<
      ProfileSection | undefined
    >(sectionsEnabled ? ProfileSection.Avatar : undefined);

    const [uploading, setIsUploading] = React.useState(false);

    const uploadFileButton = useUploadAvatar(
      setIsUploading,
      async (url) => {
        await user?.setAvatarUrl(url);
      },
      sessionStore.uid,
      !!user?.data.avatarUrl,
    );

    const userInfo = businessPlan.data.userInfo[user.uid];

    const profileQuestions = businessPlan.data.profileQuestions;
    const hasProfileQuestions = profileQuestions?.length > 0;

    if (!userInfo) {
      return null;
    }

    return (
      <YStack gap={"$medium"}>
        <SectionWithInfo
          title={t("business:section_title_looks")}
          expanded={!currentSection || currentSection === ProfileSection.Avatar}
          completed={!!userInfo.displayName}
          onPress={() => {
            if (sectionsEnabled) setCurrentSection(ProfileSection.Avatar);
          }}
          onNextPress={
            sectionsEnabled
              ? () => {
                  setCurrentSection(
                    hasProfileQuestions
                      ? ProfileSection.ProfileQuestions
                      : ProfileSection.Description,
                  );
                }
              : undefined
          }
        >
          <Box>
            <XStack gap="$small" alignItems="center">
              <Info />
              <Label>{t("business:section_label_looks")}</Label>
            </XStack>
            <XStack gap="$large" alignItems="center" flex={1}>
              {!uploading && (
                <UserAvatar userUid={user.uid} size={AvatarSize.Large} />
              )}
              {uploading && <LoadingCircular size={128} />}
              <Group borderTop={false}>
                <TitleTextarea
                  type={"inline"}
                  placeholder={t("business:profile_placeholder_display_name")}
                  icon={PencilLine}
                  iconFirst
                  blurOnSubmit
                  defaultValue={user.data.displayName ?? ""}
                  onSubmitEditing={(e) =>
                    user.setDisplayName(e.nativeEvent.text)
                  }
                  submitOnEnter
                />
                <Body>{getDayJs(user.data.lastActivity)}</Body>
              </Group>
            </XStack>
            {!isApp && <YStack gap={"$medium"}>{uploadFileButton}</YStack>}
          </Box>
        </SectionWithInfo>

        {hasProfileQuestions && (
          <SectionWithInfo
            title={t("business:section_title_questions")}
            expanded={
              !currentSection ||
              currentSection === ProfileSection.ProfileQuestions
            }
            completed={
              !!userInfo.profile &&
              Object.keys(userInfo.profile.profileQuestions).length ===
                profileQuestions.length
            }
            onPress={() => {
              if (sectionsEnabled)
                setCurrentSection(ProfileSection.ProfileQuestions);
            }}
            onNextPress={
              sectionsEnabled
                ? () => {
                    setCurrentSection(ProfileSection.Description);
                  }
                : undefined
            }
          >
            <Box>
              <XStack gap="$small" alignItems="center">
                <Info />
                <Label>{t("business:section_label_questions")}</Label>
              </XStack>

              {profileQuestions.map((question) => {
                return (
                  <YStack gap={"$medium"} key={question.uid}>
                    <Label>{question.question}</Label>

                    <BodyTextarea
                      type={"border"}
                      placeholder={t("business:profile_placeholder_answer")}
                      blurOnSubmit
                      allowEmptyTextSubmit
                      defaultValue={
                        user.data.profile?.profileQuestions[question.uid] ?? ""
                      }
                      onSubmitEditing={(e) =>
                        user.saveProfileQuestionAnswer(
                          question.uid,
                          e.nativeEvent.text.trim(),
                        )
                      }
                      submitOnEnter
                    />
                  </YStack>
                );
              })}
            </Box>
          </SectionWithInfo>
        )}

        <SectionWithInfo
          title={t("business:section_title_presentation")}
          expanded={
            !currentSection || currentSection === ProfileSection.Description
          }
          completed={!!userInfo.profile?.description}
          onPress={() => {
            if (sectionsEnabled) setCurrentSection(ProfileSection.Description);
          }}
          onNextPress={
            sectionsEnabled
              ? () => {
                  setCurrentSection(ProfileSection.Team);
                }
              : undefined
          }
        >
          <Box>
            <XStack gap="$small" alignItems="center">
              <Info />
              <Label>{t("business:section_label_presentation")}</Label>
            </XStack>

            <BodyTextarea
              type={"border"}
              placeholder={t("business:profile_placeholder_presentation")}
              minHeight={120}
              multiline={true}
              blurOnSubmit={false}
              defaultValue={user.data.profile?.description ?? ""}
              onSubmitEditing={(e) =>
                user.updateProfileData({ description: e.nativeEvent.text })
              }
              submitOnBlur
              submitOnEnter={false}
              preventNewLine={false}
            />
          </Box>
        </SectionWithInfo>

        <SectionWithInfo
          title={t("business:section_title_team")}
          expanded={!currentSection || currentSection === ProfileSection.Team}
          completed={!!businessPlan.currentTeam}
          onPress={() => {
            if (sectionsEnabled) setCurrentSection(ProfileSection.Team);
          }}
        >
          <CurrentTeamOrJoinList businessPlan={businessPlan} />
        </SectionWithInfo>
      </YStack>
    );
  },
);

export const BusinessProfileScreen = observer(({ route }: Props) => {
  const userUid = route.params.userUid;
  const businessPlanUid = route.params.businessUid;

  const { t } = useTranslation();
  const { sessionStore, usersStore, businessPlansStore } = useStores();
  const { usersApi, businessPlansApi } = useApi();

  React.useEffect(() => {
    const unsub = usersApi.watchItem(userUid);
    return () => {
      unsub();
    };
  }, [usersApi, userUid]);

  React.useEffect(() => {
    const unsub = businessPlansApi.watchItem(businessPlanUid);
    return () => {
      unsub();
    };
  }, [businessPlansApi, businessPlanUid]);

  const user = usersStore.byId.get(userUid);
  const businessPlan = businessPlansStore.byId.get(businessPlanUid);

  if (!user || !businessPlan) {
    return null;
  }

  const allowedToEditProfile = user.data.authedUsers.includes(sessionStore.uid);

  return (
    <Screen
      title={t("business:screen_title_profile")}
      theme={businessPlan.theme}
    >
      {allowedToEditProfile && (
        <BusinessProfileSettings businessPlan={businessPlan} user={user} />
      )}
    </Screen>
  );
});

export default BusinessProfileScreen;
